import { AlertTriangle, Plane, X } from "lucide-react";

/**
 * ConflictModal — shows hard conflict (block) or soft warning (travel-time).
 *
 * Props:
 *  - conflicts: { hard: [], soft: [] }
 *  - onCancel(): close without proceeding
 *  - onProceed(): user confirmed soft-warning; proceed with the action
 */
export default function ConflictModal({ conflicts, onCancel, onProceed }) {
  if (!conflicts) return null;
  const hasHard = (conflicts.hard || []).length > 0;
  const hasSoft = (conflicts.soft || []).length > 0;
  if (!hasHard && !hasSoft) return null;

  const Icon = hasHard ? AlertTriangle : Plane;
  const color = hasHard ? "#FF4500" : "#CCFF00";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="card-surface w-full max-w-lg" data-testid="conflict-modal" style={{ borderColor: color }}>
        <div className="flex items-start justify-between p-6 border-b" style={{ borderColor: color }}>
          <div className="flex items-start gap-3">
            <Icon className="w-6 h-6 shrink-0" style={{ color }} strokeWidth={1.8} />
            <div>
              <div className="label-mono mb-1" style={{ color }}>
                {hasHard ? "BOOKING CONFLICT" : "TRAVEL TIME CHECK"}
              </div>
              <h2 className="h-display text-2xl tracking-tight">
                {hasHard ? "You can't book this session" : "Are you cutting it close?"}
              </h2>
            </div>
          </div>
          <button onClick={onCancel} data-testid="conflict-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-4">
          {hasHard && (
            <div>
              <p className="text-sm text-[#E5E5E5] mb-3">
                This session overlaps with another booking. Choose a different time, or cancel the existing booking first.
              </p>
              <div className="space-y-2">
                {conflicts.hard.map((h, i) => (
                  <ConflictRow key={h.id || `${h.start}-${i}`} item={h} color="#FF4500" />
                ))}
              </div>
            </div>
          )}
          {hasSoft && (
            <div>
              <p className="text-sm text-[#E5E5E5] mb-3">
                Your sessions are back-to-back. Please make sure you&apos;ve allowed enough <span className="text-white font-semibold">travel time between venues</span> before confirming.
              </p>
              <div className="space-y-2">
                {conflicts.soft.map((s, i) => (
                  <ConflictRow key={s.id || `${s.start}-${i}`} item={s} color="#CCFF00" showGap />
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-3 border-t border-[#262626]">
            <button onClick={onCancel} data-testid="conflict-cancel"
              className="flex-1 px-4 py-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-white">
              {hasHard ? "Close" : "Cancel"}
            </button>
            {!hasHard && (
              <button onClick={onProceed} data-testid="conflict-proceed"
                className="flex-1 px-4 py-3 text-xs uppercase tracking-wider font-bold"
                style={{ background: "#CCFF00", color: "#0A0A0A" }}>
                I&apos;ve checked — proceed
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ConflictRow({ item, color, showGap }) {
  const b = item.booking || {};
  const d = b.start ? new Date(b.start) : null;
  return (
    <div className="border-l-2 pl-3 py-2" style={{ borderColor: color }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm">
          <span className="font-semibold">
            {d ? d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}
          </span>
          <span className="text-[#A3A3A3]"> · {b.duration_min} min · {b.session_type?.replace("_"," ")} ({b.sport})</span>
        </div>
        {showGap && <span className="label-mono" style={{ color }}>{item.gap_minutes} MIN GAP</span>}
      </div>
      {b.notes && <div className="text-xs text-[#A3A3A3] mt-1 truncate">{b.notes}</div>}
      <div className="text-[0.65rem] text-[#A3A3A3] mt-1 italic">{item.reason}</div>
    </div>
  );
}
